import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { INCIDENT_CATEGORY_LABELS } from '../lib/constants.js';

// ─── Incident triage (manager + owner) ────────────────────────────────────
export function IncidentTriageView({ currentUser, locations }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', severity: '' });
  const [selected, setSelected] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.status)   params.set('status', filter.status);
      if (filter.severity) params.set('severity', filter.severity);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const r = await api('GET', `/api/admin/incidents${qs}`, null, headers);
      setRows(Array.isArray(r?.incidents) ? r.incidents : []);
    } catch (_) { setRows([]); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [filter.status, filter.severity]);

  async function patch(id, patchBody) {
    try {
      await api('PUT', `/api/admin/incidents/${id}`, patchBody, headers);
      await load();
      if (selected?.id === id) {
        const fresh = (rows || []).find((r) => r.id === id);
        setSelected(fresh || null);
      }
    } catch (e) { alert(e.message || String(e)); }
  }

  function fmtWhen(s) {
    try { return new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
    catch { return s; }
  }

  const sevColor = {
    critical: { bg: '#fef2f2', fg: '#b91c1c', bd: '#fecaca' },
    high:     { bg: '#fef9ee', fg: '#8e6c33', bd: '#f5e6c8' },
    normal:   { bg: '#fafafa', fg: '#666',    bd: '#e0e0e0' },
  };

  return (
    <div className="space-y-4">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="text-base font-semibold">Incident reports</div>
            <div className="text-xs text-gray-500">
              Triage staff-submitted incident reports. Critical reports also push + email all managers + owner.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <select value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}
              style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: '6px 10px', fontSize: 13 }}>
              <option value="">All statuses</option>
              <option value="new">New</option>
              <option value="triaged">Triaged</option>
              <option value="resolved">Resolved</option>
              <option value="dismissed">Dismissed</option>
            </select>
            <select value={filter.severity} onChange={(e) => setFilter((f) => ({ ...f, severity: e.target.value }))}
              style={{ border: '1px solid #d0d4d9', borderRadius: 6, padding: '6px 10px', fontSize: 13 }}>
              <option value="">All severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? <div className="card text-sm text-gray-500">Loading…</div> : rows.length === 0 ? (
        <div className="card text-sm text-gray-500" style={{ fontStyle: 'italic' }}>No incident reports.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((r) => {
            const c = sevColor[r.severity] || sevColor.normal;
            return (
              <div key={r.id} className="card" style={{ borderLeft: `4px solid ${c.fg}`, cursor: 'pointer' }}
                onClick={() => setSelected(r)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {INCIDENT_CATEGORY_LABELS[r.category] || r.category}
                      {r.title && <span style={{ color: '#666', fontWeight: 400 }}> — {r.title}</span>}
                    </div>
                    <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                      {r.location_name} · {fmtWhen(r.created_at)} ·
                      {' '}{r.is_anonymous ? 'Anonymous' : (r.reporter_name || `User #${r.reporter_user_id}`)}
                    </div>
                    <div style={{ fontSize: 13, color: '#444', marginTop: 6,
                                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {r.description}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, fontWeight: 700,
                                      background: c.bg, color: c.fg, border: `1px solid ${c.bd}` }}>
                      {r.severity.toUpperCase()}
                    </span>
                    <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 99,
                                      background: '#fafafa', color: '#555', border: '1px solid #e0e0e0' }}>
                      {r.status}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,31,29,0.45)', zIndex: 1000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
              onClick={() => setSelected(null)}>
          <div onClick={(e) => e.stopPropagation()}
                style={{ background: '#fff', borderRadius: 12, padding: 18, maxWidth: 640, width: '100%',
                          maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>
                {INCIDENT_CATEGORY_LABELS[selected.category] || selected.category}
                {selected.title && <span style={{ color: '#666', fontWeight: 400 }}> — {selected.title}</span>}
              </div>
              <span style={{ fontSize: 11, padding: '2px 10px', borderRadius: 99,
                              background: '#fafafa', color: '#555', border: '1px solid #e0e0e0' }}>
                {selected.severity} · {selected.status}
              </span>
            </div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
              {selected.location_name} · Occurred {fmtWhen(selected.occurred_at || selected.created_at)} · Filed {fmtWhen(selected.created_at)}
              {' · '}{selected.is_anonymous ? 'Anonymous' : (selected.reporter_name || `User #${selected.reporter_user_id}`)}
            </div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, color: '#212121', padding: 10,
                            background: '#fafafa', border: '1px solid #e0e0e0', borderRadius: 8, marginBottom: 10 }}>
              {selected.description}
            </div>

            <label style={{ fontSize: 12, color: '#555', display: 'block', marginBottom: 4 }}>Manager notes</label>
            <textarea defaultValue={selected.manager_notes || ''}
              onBlur={(e) => patch(selected.id, { manager_notes: e.target.value })}
              rows={4}
              placeholder="Action taken, follow-up needed, who you contacted, etc."
              style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 8, fontSize: 13,
                        fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 10 }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {selected.status === 'new' && (
                  <button onClick={() => patch(selected.id, { status: 'triaged' })}
                    style={{ padding: '6px 14px', fontSize: 13, fontWeight: 600,
                              border: '1px solid #d0d4d9', background: '#fff', borderRadius: 6, cursor: 'pointer' }}>
                    Mark triaged
                  </button>
                )}
                {selected.status !== 'resolved' && (
                  <button onClick={() => patch(selected.id, { status: 'resolved' })}
                    style={{ padding: '6px 14px', fontSize: 13, fontWeight: 600, border: 0,
                              background: '#1e7d3a', color: '#fff', borderRadius: 6, cursor: 'pointer' }}>
                    Mark resolved
                  </button>
                )}
                {selected.status !== 'dismissed' && (
                  <button onClick={() => patch(selected.id, { status: 'dismissed' })}
                    style={{ padding: '6px 14px', fontSize: 13, fontWeight: 600,
                              border: '1px solid #d0d4d9', background: '#fafafa', color: '#666', borderRadius: 6, cursor: 'pointer' }}>
                    Dismiss
                  </button>
                )}
              </div>
              <button onClick={() => setSelected(null)}
                style={{ padding: '6px 14px', fontSize: 13, border: '1px solid #d0d4d9',
                          background: '#fff', borderRadius: 6, cursor: 'pointer' }}>Close</button>
            </div>

            {(selected.triaged_at || selected.resolved_at) && (
              <div style={{ fontSize: 11, color: '#888', marginTop: 10 }}>
                {selected.triaged_at && <>Triaged by {selected.triaged_by_name || '—'} on {fmtWhen(selected.triaged_at)}. </>}
                {selected.resolved_at && <>Resolved by {selected.resolved_by_name || '—'} on {fmtWhen(selected.resolved_at)}.</>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
